// Kampagnen på forsiden: et lille Danmarkskort med en prik pr. bane (stjerner, lås eller næste),
// rangtitlen og Dagens vagt. Og kampagneskærmen, når finalen er vundet.

import { dailyPlan, DAILY_MODS, streakNow } from '../../content/daily';
import { FINALE_LEVEL, LEVELS, levelUnlocked } from '../../content/levels';
import { COASTLINE } from '../../content/map';
import { rankOf } from '../../content/progress';
import type { Rank } from '../../content/progress';
import type { DifficultyId } from '../../sim';
import { bestStars, dailyRecord, dailyStreak, levelWon, totalStars } from '../../storage';
import type { DailyRecord } from '../../storage';
import { h } from '../dom';
import { ICON } from '../icons';
import { num, plural } from '../text';

/** Kortets udsnit i km: Jylland til Sjælland og Lolland (Bornholm er med i spillet, men ikke på en bane). */
const LANDS = COASTLINE.filter((c) => c.name !== 'Bornholm');
const BOX = (() => {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const c of LANDS)
    for (const p of c.points) {
      x0 = Math.min(x0, p.x);
      y0 = Math.min(y0, p.y);
      x1 = Math.max(x1, p.x);
      y1 = Math.max(y1, p.y);
    }
  return { x0: x0 - 6, y0: y0 - 6, w: x1 - x0 + 12, h: y1 - y0 + 12 };
})();

/** Prikkens radius i km, så den ses tydeligt i et kort på 120-220 px. */
const R = Math.round(BOX.w * 0.07);

const PATHS = LANDS.map((c) => `M${c.points.map((p) => `${Math.round(p.x)} ${Math.round(p.y)}`).join('L')}Z`).join('');

/** Finalen er vundet på en hvilken som helst sværhed. */
export function finaleWon(): boolean {
  return levelWon(FINALE_LEVEL);
}

export function currentRank(): ReturnType<typeof rankOf> {
  return rankOf(totalStars(), finaleWon());
}

/** Danmarkskortet: én prik pr. bane midt i banens udsnit. Den valgte bane har en ring. */
export function dkMap(diff: DifficultyId, selected: string): HTMLElement {
  const marks = LEVELS.map((lv) => {
    const [vx0, vy0, vx1, vy1] = lv.view;
    const x = Math.round((vx0 + vx1) / 2);
    const y = Math.round((vy0 + vy1) / 2);
    const open = levelUnlocked(lv.id, levelWon);
    const n = bestStars(lv.id, diff);
    const cls = `dk-mark${open ? '' : ' locked'}${n > 0 ? ' won' : ''}${lv.id === selected ? ' sel' : ''}`;
    return `<g class="${cls}" data-level="${lv.id}" transform="translate(${x} ${y})"><circle r="${R}"/><text y="${Math.round(R * 0.45)}" font-size="${Math.round(R * 1.25)}">${lv.number}</text></g>`;
  }).join('');
  const label = `Danmarkskort med ${LEVELS.length} baner. ${LEVELS.filter((l) => levelWon(l.id)).length} er vundet.`;
  return h('div', {
    class: 'dk-map',
    role: 'img',
    'aria-label': label,
    html: `<svg viewBox="${Math.round(BOX.x0)} ${Math.round(BOX.y0)} ${Math.round(BOX.w)} ${Math.round(BOX.h)}" aria-hidden="true"><path class="dk-land" d="${PATHS}"/>${marks}</svg>`,
  });
}

/** Linjen under rangtitlen: "7 i alt · 1 stjerne mere til Montør". Ren tekst, så break-ui kan måle den. */
export function rankSub(total: number, r: Rank): string {
  const next = r.next ? (r.next.need > 0 ? `${plural(r.next.need, 'stjerne', 'stjerner')} mere til ${r.next.title}` : `Vind bane ${LEVELS.length} for at blive ${r.next.title}`) : 'Hele kampagnen er klaret';
  return `${num(total)} i alt · ${next}`;
}

/** Undertitlen på Dagens vagt-knappen: dagens regel og bane, eller hvordan det gik i dag. */
export function dailySub(modName: string, levelTitle: string, done: DailyRecord | null, streak = 0): string {
  if (!done) return `${modName} · ${levelTitle}`;
  // Spor: tillid. Stimen står, når dagens vagt er spillet: "2 stjerner · 4 i træk".
  if (streak > 1) return `${done.won ? plural(done.stars, 'stjerne', 'stjerner') : 'Tabt i dag'} · ${streak} i træk`;
  return done.won ? `I dag: ${plural(done.stars, 'stjerne', 'stjerner')}` : 'Tabt i dag. Prøv igen';
}

/** Rangtitlen og hvad der mangler til den næste. */
export function rankBlock(): HTMLElement {
  const r = currentRank();
  return h(
    'div',
    { class: 'rank' },
    h('span', { class: 'rank-eyebrow' }, 'Din titel'),
    h('b', { class: 'rank-title' }, r.title),
    h('span', { class: 'rank-sub' }, h('span', { class: 'rank-star', html: ICON.star, 'aria-hidden': 'true' }), rankSub(totalStars(), r)),
  );
}

/** Knappen til Dagens vagt med dagens modifikator og et flueben, hvis den er spillet. */
export function dailyButton(now: Date, onclick: () => void): HTMLElement {
  const plan = dailyPlan(now);
  const done = dailyRecord(plan.key);
  const lv = LEVELS.find((l) => l.id === plan.level);
  return h(
    'button',
    { type: 'button', class: `btn daily-btn${done ? ' done' : ''}`, onclick },
    h('span', { class: 'daily-icon', html: done ? ICON.star : ICON.clock, 'aria-hidden': 'true' }),
    h(
      'span',
      { class: 'daily-text' },
      h('b', {}, `Dagens vagt #${plan.number}`),
      h('small', {}, dailySub(DAILY_MODS[plan.mod].name, lv?.title ?? '', done, streakNow(dailyStreak(), plan.key))),
    ),
  );
}

/** Kampagnepanelet øverst på forsiden. */
export function campaignPanel(diff: DifficultyId, selected: string, onDaily: () => void, now = new Date()): HTMLElement {
  return h('div', { class: 'camp' }, dkMap(diff, selected), h('div', { class: 'camp-side' }, rankBlock(), dailyButton(now, onDaily)));
}

/** Kampagneskærmen efter finalen: Danmark er forbundet, titlen og hvad der er tilbage at hente. */
export function campaignDone(diff: DifficultyId): HTMLElement {
  const r = currentRank();
  const all = LEVELS.length * 3;
  const here = LEVELS.reduce((a, l) => a + bestStars(l.id, diff), 0);
  return h(
    'section',
    { class: 'campaign-done', 'aria-label': 'Kampagnen er klaret' },
    h('p', { class: 'eyebrow' }, 'Kampagnen er klaret'),
    h('h2', {}, 'Hele Danmark hænger sammen'),
    dkMap(diff, FINALE_LEVEL),
    h('p', { class: 'campaign-rank' }, 'Ny titel: ', h('b', { class: 'rank-title' }, r.title)),
    h('p', { class: 'campaign-more' }, here < all ? `${here} af ${all} stjerner på denne sværhed. Prøv en sværere vagt, eller tag Dagens vagt.` : 'Alle stjerner er hentet. Tag Dagens vagt i morgen.'),
  );
}
